import { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { timeoutDuration } from 'utils/constants/dateInfo/time-numbers';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import {
  CasinoTournamentsType,
  ETournamentProductTypeIds,
  TournamentPlayerList
} from 'interfaces/casino-data';
import {
  getTournament,
  getTournamentStats
} from 'services/casino/casino-tournaments';
import { DesktopTournamentSingleViewSkeleton } from './Desktop/Skeleton';
import { Viewport } from 'components/Viewport';
import { useMounted } from 'hooks/useMounted';
import {
  getCachedTournamentsData,
  getTournamentActiveProductTypeId
} from 'store/selectors/casino-data';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

const { MobileTournamentSingleView } = lazy(
  () =>
    import(
      /* webpackChunkName: "mobile-casino-tournaments-single" */ './Mobile'
    )
);

const { DesktopTournamentSingleView } = lazy(
  () =>
    import(
      /* webpackChunkName: "desktop-casino-tournaments-single" */ './Desktop'
    )
);

type Props = {
  notNeedSkeleton?: boolean;
  fromIndex?: boolean;
};

export const TournamentSingleView = function ({
  notNeedSkeleton,
  fromIndex
}: Props): ReactElement {
  const tournaments = useSelector(getCachedTournamentsData);
  const activeProductTypeId = useSelector(getTournamentActiveProductTypeId);

  const queries = new URLSearchParams(window.location.search);

  const { mounted } = useMounted();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const user = useSelector(getUser);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [tournamentData, setTournamentData] =
    useState<CasinoTournamentsType | null>(null);

  const [topPlayerList, setTopPlayerList] =
    useState<TournamentPlayerList | null>(null);

  const isSportsTournament =
    tournamentData?.ProductTypeId === ETournamentProductTypeIds.SPORT;

  const tournamentId = queries.get('tournamentId');

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (
      !user.pending &&
      !notNeedSkeleton &&
      (isMobile() || !fromIndex) &&
      tournamentId
    ) {
      setVisible(true);

      if (activeProductTypeId && tournaments[activeProductTypeId].length) {
        const tournament = tournaments[activeProductTypeId].find(
          item => item.Id === +tournamentId
        );

        if (tournament) {
          setTournamentData(tournament);
        }
      }

      setLoading(true);
      getTournament(
        +tournamentId,
        (data: { result: CasinoTournamentsType }) => {
          if (!mounted.current) {
            return;
          }

          setLoading(false);
          setTournamentData(data.result);
        }
      );
    } else if (tournamentData) {
      setTournamentData(null);
    }

    if (!tournamentId && visible) {
      setVisible(false);
    }
  }, [location.search, !!user.pending, isLoggedIn, tournamentId]);

  useEffect(() => {
    if (!isMobile() && fromIndex) {
      return;
    }

    const interval = setInterval(() => {
      if (tournamentId) {
        getTournamentStats(
          +tournamentId,
          (data: { result: [TournamentPlayerList] }) => {
            setTopPlayerList(data?.result?.[0]);
          }
        );
      }
    }, timeoutDuration.DURATION_30000);

    return () => {
      interval && clearInterval(interval);
    };
  }, [visible]);

  return (
    <Viewport
      desktop={DesktopTournamentSingleView}
      mobile={MobileTournamentSingleView}
      fallback={
        notNeedSkeleton || fromIndex ? (
          <div />
        ) : (
          <DesktopTournamentSingleViewSkeleton />
        )
      }
      innerProps={{
        tournamentData,
        visible,
        setVisible,
        loading,
        isSportsTournament,
        fromIndex,
        topPlayerList
      }}
    />
  );
};
